/**
 * Notification feed — spec §8 step 1, §9.
 *
 * Depletion is meant to register as an event rather than something the player
 * discovers later by noticing throughput fell (spec §13 item 3 keeps
 * auto-routing off by default for exactly this reason). This is where it
 * registers.
 *
 * Newest first, and capped: over a three-hour session the notification list
 * grows without bound in state, but only the recent tail is worth showing.
 */

import type { GameState } from '../../sim/types';
import { el, section, setText, type Panel } from './panel';

const VISIBLE = 12;

export function createNotificationPanel(): Panel {
  const { root, body } = section('Events');

  const empty = el('div', 'hint', 'Nothing yet.');
  const list = el('div', 'event-list');
  body.append(empty, list);

  const rows = Array.from({ length: VISIBLE }, () => {
    const row = el('div', 'event-row');
    const tick = el('span', 'event-tick');
    const message = el('span', 'event-message');
    row.append(tick, message);
    list.append(row);
    return { root: row, tick, message };
  });

  return {
    root,
    update(state: GameState): void {
      const recent = state.notifications.slice(-VISIBLE).reverse();
      empty.classList.toggle('hidden', recent.length > 0);

      for (let i = 0; i < VISIBLE; i++) {
        const row = rows[i];
        if (row === undefined) continue;
        const note = recent[i];

        if (note === undefined) {
          row.root.classList.add('hidden');
          continue;
        }

        row.root.classList.remove('hidden');
        row.root.classList.toggle('warn', note.kind === 'hexDepleted');
        setText(row.tick, String(note.tick));
        setText(row.message, note.message);
      }
    },
  };
}
